import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Eye, Building2, Users, AlertTriangle, Download, Upload,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Button } from '../../components/Shared/Button';
import { Badge } from '../../components/Shared/Badge';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Pagination } from '../../components/Shared/Pagination';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import { ExcelImportModal } from '../../components/Shared/ExcelImportModal';
import { useExcelIo } from '../../hooks/useExcelIo';
import { useUrlListState } from '../../hooks/useUrlListState';
import ObjectEditSlideOver from './components/ObjectEditSlideOver';

import { ListPageShell } from '../../components/Lists/ListPageShell';
import { KpiStripList } from '../../components/Lists/KpiStripList';
import { ListToolbar } from '../../components/Lists/ListToolbar';
import { ListGrid } from '../../components/Lists/ListGrid';
import { ServicePill } from '../../components/Lists/ServicePill';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';

import { useCustomerStore } from '../../store/useCustomerStore';
import { useObjectStore } from '../../store/useObjectStore';
import { useTaskStore } from '../../store/useTaskStore';
import { usePlanningStore } from '../../store/usePlanningStore';
import { useDueTasksStore } from '../../store/useDueTasksStore';
import { useToastStore } from '../../store/useToastStore';
import { PlannedBar, PlannedValue } from './components/ObjectPlannedBar';
import { HelpPopover } from '../../components/Shared/HelpPopover';
import { getISOWeek, getISOWeekYear } from 'date-fns';

import { getObjectColors } from '../../utils/objectColors';
import {
  getMondayOfWeek,
  formatHoursDecimal,
} from '../../utils/dashboardData';
import { isoDate } from '../../utils/dateUtils';
import {
  aggregateObjectWeek,
  summarizeObjectsPlanned,
  sortObjectList,
  OBJECT_SORT_IDS,
  type ObjectSortId,
} from '../../utils/listData';

import type {
  FacilityFlowCoreModulesCustomersDtosObjectDto,
  FacilityFlowCoreModulesCustomersDtosCreateObjectRequest,
} from '../../api/generated/models';
import type { UpdateObjectRequestWithGeo } from '../../types/objects-api';
import { isStandingInvoice, objectBillingLabel, type ObjectBilling } from '../../types/billing';
import { OBJECT_TYPES, objectTypeLabelKey, toObjectType, type ObjectType } from '../../types/objectType';
import type { KpiItem, SortOption } from '../../types/lists';
import type { TaskListDto } from '../../types';

type ObjectDto = FacilityFlowCoreModulesCustomersDtosObjectDto & {
  latitude?: number | null;
  longitude?: number | null;
};

const COLUMNS_ACTIVE = '4px minmax(0,1.6fr) minmax(0,1fr) 90px 110px 160px 112px';
const COLUMNS_PENDING = '4px minmax(0,1.6fr) minmax(0,1fr) minmax(0,1fr) 130px 112px';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const SELECT_CLASS =
  'px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12.5px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';

export default function ObjectsPage() {
  const { t } = useTranslation(['objects', 'common']);
  const navigate = useNavigate();
  const { addToast } = useToastStore();

  const {
    objects,
    loading,
    error,
    fetchAllObjects,
    deleteObject,
    getObjectById,
    selectedObject,
    updateObject,
    createObject,
  } = useObjectStore();

  const { customers, fetchCustomers } = useCustomerStore();
  const { tasks, fullListLoaded: allTasksLoaded, fetchTasks } = useTaskStore();
  const { entries: planningEntries, fetchWeekEntries } = usePlanningStore();
  const { objectDueMinutes, fetchObjectDueMinutes } = useDueTasksStore();

  const [listState, setListState] = useUrlListState(
    {
      q: '',
      customer: 'all',
      type: 'all' as ObjectType | 'all',
      sort: 'name' as ObjectSortId,
      page: 1,
    },
    { type: ['all', ...OBJECT_TYPES], sort: OBJECT_SORT_IDS },
  );
  const { q: search, customer: filterCustomerId, type: filterType, sort: sortBy, page: activePage } = listState;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // why: section and utilization come from all tasks and this week's planning entries. The planning
  // page loads other weeks into the same store, and on a first visit no tasks are loaded yet;
  // rendering before both are in would move objects between the active and pending sections.
  const [weekEntriesLoaded, setWeekEntriesLoaded] = useState(false);
  const [tasksRefreshed, setTasksRefreshed] = useState(false);

  const reloadObjects = useCallback(
    () =>
      fetchAllObjects({
        customerId: filterCustomerId !== 'all' ? filterCustomerId : undefined,
        search: search.trim() || undefined,
        type: filterType !== 'all' ? filterType : undefined,
      }),
    [fetchAllObjects, filterCustomerId, search, filterType],
  );

  const handleCustomerFilterChange = (value: string) => setListState({ customer: value });
  const handleTypeFilterChange = (value: string) => setListState({ type: toObjectType(value) ?? 'all' });
  const handleSearchChange = (value: string) => setListState({ q: value });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [objectToEditId, setObjectToEditId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { exporting, exportExcel } = useExcelIo('objects');

  const today = useMemo(() => new Date(), []);
  const weekRange = useMemo(() => {
    const monday = getMondayOfWeek(today);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: isoDate(monday), end: isoDate(sunday) };
  }, [today]);

  useEffect(() => {
    fetchCustomers({ pageSize: 1000 });
    fetchTasks().then(() => setTasksRefreshed(true));
    // A failed planning load still shows the list, just without utilization.
    fetchWeekEntries(weekRange.start, weekRange.end)
      .catch(() => undefined)
      .finally(() => setWeekEntriesLoaded(true));
    // why: without due minutes every object shows "—"; the list itself stays usable.
    fetchObjectDueMinutes(getISOWeekYear(today), getISOWeek(today)).catch(() => undefined);
  }, [fetchCustomers, fetchTasks, fetchWeekEntries, fetchObjectDueMinutes, today, weekRange.start, weekRange.end]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      reloadObjects().then(() => setInitialLoadComplete(true));
    }, 350);
    return () => window.clearTimeout(handle);
  }, [reloadObjects]);

  const tasksByObject = useMemo(() => {
    const map = new Map<string, TaskListDto[]>();
    for (const task of tasks) {
      if (!task.objectId) continue;
      const arr = map.get(task.objectId) ?? [];
      arr.push(task);
      map.set(task.objectId, arr);
    }
    return map;
  }, [tasks]);

  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customers) {
      if (c.id) map.set(c.id, c.name ?? '—');
    }
    return map;
  }, [customers]);

  const aggregates = useMemo(() => {
    const map = new Map<string, ReturnType<typeof aggregateObjectWeek>>();
    for (const o of objects) {
      if (!o.id) continue;
      map.set(o.id, aggregateObjectWeek(o.id, planningEntries, tasksByObject, objectDueMinutes[o.id]));
    }
    return map;
  }, [objects, planningEntries, tasksByObject, objectDueMinutes]);

  const activeObjects = useMemo(
    () =>
      objects.filter((o) => {
        if ((o.status ?? '').toLowerCase() === 'inactive') return false;
        const agg = aggregates.get(o.id ?? '');
        return agg?.hasPlan ?? true;
      }),
    [objects, aggregates],
  );

  const pendingPlanObjects = useMemo(
    () =>
      sortObjectList(
        objects.filter((o) => {
          if ((o.status ?? '').toLowerCase() === 'inactive') return false;
          const agg = aggregates.get(o.id ?? '');
          return !(agg?.hasPlan ?? true);
        }),
        'name',
        aggregates,
      ),
    [objects, aggregates],
  );

  const sortedActive = useMemo(
    () => sortObjectList(activeObjects, sortBy, aggregates),
    [activeObjects, sortBy, aggregates],
  );

  // Export follows the filter, so the file mirrors what the list shows. Both lists are
  // included: the page splits objects into active and pending-plan sections, and a user
  // who filtered by customer expects the whole customer, not just one section.
  const exportIds = useMemo(() => {
    const shown = [...sortedActive, ...pendingPlanObjects];
    return shown.length === objects.length
      ? undefined
      : shown.map((o) => o.id).filter((id): id is string => !!id);
  }, [sortedActive, pendingPlanObjects, objects.length]);

  const activePageCount = Math.max(1, Math.ceil(sortedActive.length / PAGE_SIZE));
  const safeActivePage = Math.min(activePage, activePageCount);
  const pagedActive = useMemo(
    () => sortedActive.slice((safeActivePage - 1) * PAGE_SIZE, safeActivePage * PAGE_SIZE),
    [sortedActive, safeActivePage],
  );

  const goToActivePage = (next: number) => {
    setListState({ page: Math.max(1, Math.min(activePageCount, next)) });
  };

  const hasActiveFilter = filterCustomerId !== 'all' || filterType !== 'all' || search.trim().length > 0;

  const kpis: KpiItem[] = useMemo(() => {
    const totalCount = objects.length;
    const activeCount = activeObjects.length;
    let activeTasksTotal = 0;
    let weeklyHoursTotal = 0;
    for (const o of objects) {
      const agg = aggregates.get(o.id ?? '');
      if (!agg) continue;
      activeTasksTotal += agg.activeTasks;
      weeklyHoursTotal += agg.weeklyHours;
    }
    // why: only the objects the list shows as active; inactive ones may still have due tasks.
    const avgPlanned = summarizeObjectsPlanned(activeObjects.flatMap((o) => aggregates.get(o.id ?? '') ?? []));
    return [
      {
        label: t('objects:kpis.active'),
        value: activeCount,
        sub: t('objects:kpis.ofTotal', { count: totalCount }),
      },
      { label: t('objects:kpis.tasksActive'), value: activeTasksTotal },
      {
        label: t('objects:kpis.hPerWeek'),
        value: formatHoursDecimal(weeklyHoursTotal),
        unit: 'h',
      },
      {
        label: t('objects:kpis.avgPlanned'),
        value: avgPlanned ?? '—',
        unit: avgPlanned === null ? undefined : '%',
        help: { label: t('common:actions.showExplanation'), text: t('objects:kpis.avgPlannedHelp') },
      },
    ];
  }, [objects, activeObjects, aggregates, t]);

  const sortOptions: SortOption[] = useMemo(
    () => [
      { id: 'name', label: t('objects:sort.name') },
      { id: 'planned', label: t('objects:sort.planned') },
      { id: 'hours', label: t('objects:sort.hours') },
    ],
    [t],
  );

  const openDeleteDialog = (obj: ObjectDto) => {
    if (!obj.id) return;
    setObjectToDelete({ id: obj.id, name: obj.name || t('objects:title') });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!objectToDelete) return;
    try {
      await deleteObject(objectToDelete.id);
      addToast(t('objects:messages.deleted'), 'success');
      await reloadObjects();
    } catch {
      addToast(t('common:errors.deleteFailed'), 'error');
    } finally {
      setIsDeleteDialogOpen(false);
      setObjectToDelete(null);
    }
  };

  const openEdit = async (id: string) => {
    setObjectToEditId(id);
    await getObjectById(id);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setObjectToEditId(null);
  };

  // A complete task list from an earlier visit is good enough; it refreshes in place.
  const aggregateSourcesReady = weekEntriesLoaded && (allTasksLoaded || tasksRefreshed);
  if ((!initialLoadComplete || !aggregateSourcesReady) && !error) {
    return <ObjectsSkeleton />;
  }
  if (error && !initialLoadComplete) {
    return (
      <ListPageShell kicker={t('objects:kicker')} title={t('objects:title')}>
        <EmptyState
          variant="error"
          title={t('objects:messages.loadError')}
          description={error}
          onRetry={() => reloadObjects()}
        />
      </ListPageShell>
    );
  }

  return (
    <div>
      <ListPageShell
        kicker={t('objects:kicker')}
        title={t('objects:title')}
        meta={t('objects:meta', {
          count: objects.length,
          active: activeObjects.length,
          pending: pendingPlanObjects.length,
        })}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => exportExcel(exportIds)}
              loading={exporting}
              leftIcon={<Download className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {exporting
                ? t('common:excelImport.exporting')
                : exportIds
                  ? t('common:excelImport.exportFiltered', { total: exportIds.length })
                  : t('objects:actions.exportExcel')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsImportOpen(true)}
              disabled={exporting}
              leftIcon={<Upload className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('objects:actions.importExcel')}
            </Button>
            <Button
              onClick={() => setIsCreateOpen(true)}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('objects:newObject')}
            </Button>
          </>
        }
      >
        <KpiStripList items={kpis} />
        <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
          <ListToolbar
            placeholder={t('objects:placeholders.search')}
            search={search}
            onSearchChange={handleSearchChange}
            sortValue={sortBy}
            onSortChange={(id) => setListState({ sort: id as ObjectSortId })}
            sortOptions={sortOptions}
            extra={
              <>
                <select
                  value={filterCustomerId}
                  onChange={(e) => handleCustomerFilterChange(e.target.value)}
                  aria-label={t('objects:filter.customer')}
                  className={SELECT_CLASS}
                >
                  <option value="all">{t('objects:filter.allCustomers')}</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  value={filterType}
                  onChange={(e) => handleTypeFilterChange(e.target.value)}
                  aria-label={t('objects:filter.type')}
                  className={SELECT_CLASS}
                >
                  <option value="all">{t('objects:filter.allTypes')}</option>
                  {OBJECT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(objectTypeLabelKey(type))}
                    </option>
                  ))}
                </select>
              </>
            }
          />
        </div>

        {objects.length === 0 ? (
          <EmptyState
            icon={<Building2 className="w-12 h-12" />}
            title={hasActiveFilter ? t('objects:empty.titleFiltered') : t('objects:empty.title')}
            description={
              hasActiveFilter
                ? t('objects:empty.descriptionFiltered')
                : t('objects:empty.description')
            }
          />
        ) : (
          <div className="space-y-6">
            {/* Aktiv-Block */}
            {sortedActive.length > 0 && (
              <div>
                <SectionHeading label={t('objects:sections.active')} count={sortedActive.length} />
                {/* Desktop ListGrid */}
                <div className="hidden md:block">
                  <ListGrid columns={COLUMNS_ACTIVE} ariaLabel={t('objects:sections.active')}>
                    <ListGrid.Header>
                      <span />
                      <span>{t('objects:list.object')}</span>
                      <span>{t('objects:list.customer')}</span>
                      <span className="text-right">{t('objects:list.tasks')}</span>
                      <span className="text-right">{t('objects:list.hoursPerWeek')}</span>
                      <span className="flex items-center gap-1.5">
                        {t('objects:list.planned')}
                        <HelpPopover label={t('common:actions.showExplanation')} text={t('objects:list.plannedHelp')} />
                      </span>
                      <span />
                    </ListGrid.Header>
                    {pagedActive.map((o) => (
                      <ActiveRow
                        key={o.id}
                        o={o}
                        customerName={customerNameById.get(o.customerId ?? '') ?? '—'}
                        agg={aggregates.get(o.id ?? '')}
                        tasks={tasksByObject.get(o.id ?? '') ?? []}
                        onView={() => o.id && navigate(`/objects/${o.id}`)}
                        onEdit={() => o.id && openEdit(o.id)}
                        onDelete={() => openDeleteDialog(o as ObjectDto)}
                        t={t}
                      />
                    ))}
                  </ListGrid>
                </div>
                {/* Mobile Card-Stack */}
                <div className="md:hidden space-y-2">
                  <div className="flex items-center justify-end gap-1.5 text-[11px] text-slate-500">
                    {t('objects:list.planned')}
                    <HelpPopover label={t('common:actions.showExplanation')} text={t('objects:list.plannedHelp')} />
                  </div>
                  {pagedActive.map((o) => (
                    <MobileRow
                      key={o.id}
                      o={o}
                      customerName={customerNameById.get(o.customerId ?? '') ?? '—'}
                      agg={aggregates.get(o.id ?? '')}
                      onClick={() => o.id && navigate(`/objects/${o.id}`)}
                      t={t}
                    />
                  ))}
                </div>
                <Pagination
                  page={safeActivePage}
                  pageCount={activePageCount}
                  totalCount={sortedActive.length}
                  onPageChange={goToActivePage}
                  disabled={loading}
                />
              </div>
            )}

            {/* Pending-Plan-Block */}
            {pendingPlanObjects.length > 0 && (
              <div>
                <SectionHeading
                  label={t('objects:sections.pendingPlan')}
                  count={pendingPlanObjects.length}
                  tone="warn"
                />
                <div className="hidden md:block">
                  <ListGrid
                    columns={COLUMNS_PENDING}
                    ariaLabel={t('objects:sections.pendingPlan')}
                    className="!border-amber-300"
                  >
                    {pendingPlanObjects.map((o) => (
                      <ListGrid.Row key={o.id} onClick={() => o.id && navigate(`/objects/${o.id}`)}>
                        <div style={{ background: getObjectColors(o.id ?? o.name).color }} />
                        <div className="px-3 py-3 min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {o.name ?? '—'}
                            <BillingIntervalTag billing={o} t={t} className="ml-2" />
                            <ObjectTypeTag type={o.type} t={t} className="ml-2" />
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {formatAddress(o)}
                          </div>
                        </div>
                        <div className="px-3 py-3 text-slate-600 truncate">
                          {customerNameById.get(o.customerId ?? '') ?? '—'}
                        </div>
                        <div className="px-3 py-3 text-[12px] text-amber-700 inline-flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                          {t('objects:list.noPlanAssigned')}
                        </div>
                        <div className="px-3 py-3 flex items-center">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/planning?objectId=${o.id ?? ''}`);
                            }}
                          >
                            {t('objects:list.createPlan')}
                          </Button>
                        </div>
                        <div className="px-1 py-3 flex items-center justify-end">
                          <RowActions
                            onView={() => o.id && navigate(`/objects/${o.id}`)}
                            onEdit={() => o.id && openEdit(o.id)}
                            onDelete={() => openDeleteDialog(o as ObjectDto)}
                            t={t}
                          />
                        </div>
                      </ListGrid.Row>
                    ))}
                  </ListGrid>
                </div>
                <div className="md:hidden space-y-2">
                  {pendingPlanObjects.map((o) => (
                    <div
                      key={o.id}
                      onClick={() => o.id && navigate(`/objects/${o.id}`)}
                      className="relative overflow-hidden rounded-xl border border-amber-300 bg-white px-3 py-2.5 cursor-pointer"
                    >
                      <div
                        className="absolute inset-y-0 left-0 w-1"
                        style={{ background: getObjectColors(o.id ?? o.name).color }}
                        aria-hidden
                      />
                      <div className="pl-3">
                        <div className="font-medium text-slate-900">
                          {o.name ?? '—'}
                          <BillingIntervalTag billing={o} t={t} className="ml-2" />
                          <ObjectTypeTag type={o.type} t={t} className="ml-2" />
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {customerNameById.get(o.customerId ?? '') ?? '—'}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" strokeWidth={2} aria-hidden />
                            {t('objects:list.noPlanAssigned')}
                          </span>
                          <Button
                            variant="primary"
                            size="sm"
                            className="ml-auto"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/planning?objectId=${o.id ?? ''}`);
                            }}
                          >
                            {t('objects:list.createPlan')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </ListPageShell>

      {/* Excel round-trip: upload the edited export, preview, then apply */}
      <ExcelImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        entity="objects"
        title={t('objects:actions.importTitle')}
        onImported={() => fetchAllObjects()}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setObjectToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('objects:deleteObject')}
        message={t('objects:confirmDelete', { name: objectToDelete?.name || '' })}
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
      />

      {isEditOpen && selectedObject && objectToEditId && (
        <ObjectEditSlideOver
          isOpen
          onClose={closeEdit}
          mode="edit"
          object={selectedObject}
          onSave={async (payload: UpdateObjectRequestWithGeo) => {
            await updateObject(objectToEditId, payload);
            await reloadObjects();
            closeEdit();
          }}
        />
      )}

      {isCreateOpen && (
        <ObjectEditSlideOver
          isOpen
          onClose={() => setIsCreateOpen(false)}
          mode="create"
          customers={customers}
          onSave={async (payload) => {
            await createObject(payload as FacilityFlowCoreModulesCustomersDtosCreateObjectRequest);
            await reloadObjects();
            setIsCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}

function formatAddress(o: ObjectDto): string {
  const parts: string[] = [];
  const street = o.address?.street;
  const number = o.address?.streetNumber;
  if (street) parts.push(`${street}${number ? ` ${number}` : ''}`);
  const city = [o.address?.postalCode, o.address?.city].filter(Boolean).join(' ');
  if (city) parts.push(city);
  return parts.join(', ') || '—';
}

function SectionHeading({
  label,
  count,
  tone = 'default',
}: {
  label: string;
  count: number;
  tone?: 'default' | 'warn';
}) {
  const toneText = tone === 'warn' ? 'text-amber-600' : 'text-slate-400';
  const toneBorder = tone === 'warn' ? 'border-amber-300/60' : 'border-slate-200';
  return (
    <div
      className={`font-mono-num text-[10.5px] font-semibold uppercase tracking-[0.18em] ${toneText} mb-2 flex items-center gap-2`}
    >
      <span>{label}</span>
      <span className="text-slate-500">· {count}</span>
      <span className={`flex-1 border-t ${toneBorder} ml-2`} />
    </div>
  );
}

interface ActiveRowProps {
  o: ObjectDto;
  customerName: string;
  agg: ReturnType<typeof aggregateObjectWeek> | undefined;
  tasks: TaskListDto[];
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/** Short tag for standing-invoice objects (FACF-106, FAFD-27); monthly is the default and stays silent. */
function BillingIntervalTag({
  billing, t, className,
}: { billing: ObjectBilling; t: (key: string, options?: Record<string, unknown>) => string; className?: string }) {
  if (!isStandingInvoice(billing.billingInterval)) return null;
  return <Badge variant="default" className={className}>{objectBillingLabel(t, billing)}</Badge>;
}

/** Type of the object (Stiegenhaus, Büro, …); objects without a type show nothing. */
function ObjectTypeTag({
  type, t, className,
}: { type?: string | null; t: (key: string) => string; className?: string }) {
  const objectType = toObjectType(type);
  if (!objectType) return null;
  return <Badge variant="default" className={className}>{t(objectTypeLabelKey(objectType))}</Badge>;
}

function ActiveRow({ o, customerName, agg, tasks, onView, onEdit, onDelete, t }: ActiveRowProps) {
  const colors = getObjectColors(o.id ?? o.name ?? '');
  const services = useUniqueServices(tasks);

  return (
    <ListGrid.Row onClick={onView}>
      <div style={{ background: colors.color }} aria-hidden />
      <div className="px-3 py-3 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 truncate">
            {o.name ?? '—'}
          </span>
          <BillingIntervalTag billing={o} t={t} />
          <ObjectTypeTag type={o.type} t={t} />
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {formatAddress(o)}
        </div>
        {services.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            {services.slice(0, 4).map((s) => (
              <ServicePill key={s.id} serviceId={s.id} serviceName={s.name} />
            ))}
          </div>
        )}
      </div>
      <div className="px-3 py-3 min-w-0">
        <div className="text-slate-600 truncate">{customerName}</div>
        {agg && agg.employeeCount > 0 && (
          <div className="text-[10.5px] text-slate-400 mt-0.5 flex items-center gap-1 font-mono-num">
            <Users className="w-3 h-3" strokeWidth={2} aria-hidden />
            {agg.employeeCount} {t('objects:list.employeesShort')}
          </div>
        )}
      </div>
      <div className="px-3 py-3 text-right font-mono-num text-slate-900">
        {agg?.activeTasks ?? 0}
      </div>
      <div className="px-3 py-3 text-right font-mono-num text-slate-600">
        {formatHoursDecimal(agg?.weeklyHours ?? 0)}
      </div>
      <div className="px-3 py-3 flex flex-col justify-center gap-1 min-w-0">
        <PlannedValue agg={agg} color={colors.color} />
        <PlannedBar agg={agg} color={colors.color} />
      </div>
      <div className="px-1 py-3 flex items-center justify-end">
        <RowActions onView={onView} onEdit={onEdit} onDelete={onDelete} t={t} />
      </div>
    </ListGrid.Row>
  );
}

function MobileRow({
  o,
  customerName,
  agg,
  onClick,
  t,
}: {
  o: ObjectDto;
  customerName: string;
  agg: ReturnType<typeof aggregateObjectWeek> | undefined;
  onClick: () => void;
  t: (key: string) => string;
}) {
  const colors = getObjectColors(o.id ?? o.name ?? '');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="relative overflow-hidden rounded-xl border border-slate-200 bg-white px-3 py-2.5 active:bg-slate-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A871]/40"
    >
      <div
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: colors.color }}
        aria-hidden
      />
      <div className="pl-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-slate-900 text-[14px] truncate">
              {o.name ?? '—'}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {customerName}
            </div>
          </div>
          <PlannedValue agg={agg} color={colors.color} />
        </div>
        <PlannedBar agg={agg} color={colors.color} className="mt-2" />
        <div className="flex items-center justify-between mt-2 text-[10.5px] text-slate-500 font-mono-num">
          <span>
            {agg?.activeTasks ?? 0} {t('objects:list.tasks')} · {formatHoursDecimal(agg?.weeklyHours ?? 0)}h
          </span>
        </div>
      </div>
    </div>
  );
}

function RowActions({
  onView,
  onEdit,
  onDelete,
  t,
}: {
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  t: (key: string) => string;
}) {
  return (
    <div
      className="opacity-60 group-hover:opacity-100 group-focus-within:opacity-100 flex items-center gap-0.5 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onView}
        title={t('objects:actions.viewDetails')}
        aria-label={t('objects:actions.viewDetails')}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Eye className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onEdit}
        title={t('objects:actions.edit')}
        aria-label={t('objects:actions.edit')}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Pencil className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onDelete}
        title={t('objects:actions.delete')}
        aria-label={t('objects:actions.delete')}
        className="w-7 h-7 grid place-items-center rounded text-red-600 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}

function useUniqueServices(tasks: TaskListDto[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const t of tasks) {
    if (!t.serviceId) continue;
    if (!seen.has(t.serviceId)) seen.set(t.serviceId, t.serviceName ?? '');
  }
  return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
}

function ObjectsSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 animate-pulse">
      <div className="h-[88px] rounded-xl border border-slate-200 bg-white" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl border border-slate-200 bg-white" />
        ))}
      </div>
      <div className="h-12 rounded-xl border border-slate-200 bg-white" />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 border-t border-slate-100 first:border-t-0 bg-slate-50/50" />
        ))}
      </div>
    </div>
  );
}
